import { all, call, put, takeEvery, take, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { isAIMode, isApplicationLoading } from 'src/selectors';
import { noPermissions as noPermissionsAlert } from 'src/components/alert/slice';
import { getResources } from 'src/services/coamClient';
import { RELOAD } from 'src/store/actions/actionTypes';
import {
    completeLoadingApplication,
    setResources,
    loadApplication,
    setTenant,
    setUserSettings,
    confirmImageAI,
    changeMode,
    toggleShowCreateSceneModal,
    setAIModalOpen,
} from 'src/slice';
import { noSurfacesEnabled } from 'src/settings';
import { logError } from 'src/loggingManager';
import { getPurposeSettings } from './services/linkClient';
import idGeneratorAtor from './util/idGeneratorAtor';
import { uploadBackgrounds } from './pages/editor/components/backgrounds/slice';
import { getAllBackgrounds } from './selectors/layers';

const idGenerator = idGeneratorAtor('BackgroundButton');
const idAddGenerator = idGeneratorAtor('aiNewBackground');

type CoamResources = Record<string, string[]>;

type SetResourcesPayload = {
    resources: CoamResources;
    areSurfacesRequired: boolean;
    type?: string;
    id?: string;
};

type SetTenantPayload = {
    type: string;
    id: string;
};

function* initializeCoam(): Generator<any, any, any> {
    // Same containment concern as getTenantMerchandisingSettings below: this runs unconditionally
    // once at boot via loadApp/takeEvery(loadApplication, ...), itself inside applicationSagas()'s
    // own all([...]) — a sibling of editorSagas() in the app's root saga. An uncaught error here
    // would cancel every saga in the entire app, not just fail to load COAM resources.
    let resources: CoamResources;
    try {
        resources = yield* call(getResources);
    } catch (error) {
        logError(`Failed to load COAM resources, continuing without them: ${error}`);
        return yield* put(noPermissionsAlert());
    }
    if (!Object.keys(resources).length) {
        return yield* put(noPermissionsAlert());
    }
    const actionPayload: SetResourcesPayload = {
        resources,
        areSurfacesRequired: noSurfacesEnabled ? localStorage.getItem('settingNoSurfaces') !== 'true' : true,
    };

    // Pull the last selected coam option and check that the user has permission for it.
    let tenantId: string | undefined;
    let tenantType: string | undefined;
    const coam = localStorage.getItem('coamSelection');
    if (coam) {
        [tenantType, tenantId] = coam.split(',');
    }
    if (tenantType && resources[tenantType] && resources[tenantType].includes(tenantId!)) {
        Object.assign(actionPayload, {
            type: tenantType,
            id: tenantId,
        });
    } else {
        // If there is at least one tenant type and it's ids aren't empty populate the display
        const firstTenantType = Object.keys(resources).find((key) => resources[key].length);
        if (firstTenantType) {
            Object.assign(actionPayload, { type: firstTenantType, id: resources[firstTenantType][0] });
        }
    }

    return yield* put(setResources(actionPayload as any));
}

function* loadApp(): Generator<any, void, any> {
    const preloadState = localStorage.getItem('expiredState');
    if (preloadState) {
        localStorage.removeItem('expiredState');
        yield* put({ type: RELOAD, payload: JSON.parse(preloadState) });
    } else {
        yield* call(initializeCoam);
    }
    yield* put(completeLoadingApplication());
}

// This function GUARANTEES that, when this is "yield call()"ed, that the application load will be complete once it is done yielding.
export function* awaitApplicationLoad(): Generator<any, void, any> {
    const loading = yield* select(isApplicationLoading);
    if (loading) {
        yield* take(completeLoadingApplication);
    }
}

// getPurposeSettings already logs+rethrows its own failures (see linkClient.ts) — but this
// generator is invoked via takeEvery from a shared `all([...])` alongside every other top-level
// saga (applicationSagas, and above that, the app's root saga alongside editorSagas). Per
// redux-saga's fail-fast `all()` semantics, an uncaught error here doesn't just fail this one
// settings fetch — it cancels EVERY sibling task in every enclosing `all()`, silently killing the
// entire saga layer (every watcher in editorSagas, including background/document/canvas uploads,
// rendering, saving — everything) with no visible symptom beyond the original error, which itself
// may be filtered/collapsed in the console. Confirmed live: a 403 from getPurposeSettings (at the
// time, caused by the separate, since-reverted RNL-1221 SDK migration routing this call to a
// dev/stage settings domain the tenant lacked permission for, instead of the hardcoded-to-
// production endpoint restored below) silently cancelled the entire app's sagas, making every
// upload/save/render action appear to "hang" with zero network activity and zero visible error.
// That specific trigger is gone now that RNL-1221 is reverted, but the containment itself is not
// about that one cause — this fetch failing, for any reason, must never be allowed to take down
// anything else.
function* getTenantMerchandisingSettings({ payload }: PayloadAction<SetTenantPayload>): Generator<any, void, any> {
    if (payload && payload.type && payload.id) {
        try {
            const data: any = yield* call(getPurposeSettings, payload.type, payload.id);
            yield* put(setUserSettings(data));
        } catch (error) {
            logError(`Failed to load tenant merchandising settings, continuing without them: ${error}`);
        }
    }
}

function* changeModals({ payload }: PayloadAction<string>): Generator<any, void, any> {
    const isModalOpen = yield* select(isAIMode);
    yield* put(setAIModalOpen(payload === 'ai'));
    if ((isModalOpen && payload !== 'ai') || (!isModalOpen && payload === 'ai')) {
        yield* put(toggleShowCreateSceneModal());
    }
}

function* createSceneFromAI({ payload }: PayloadAction<string>): Generator<any, void, any> {
    if (payload) {
        const response: Response = yield* call(fetch, payload);
        const blob: Blob = yield* call([response, response.blob]);
        (blob as any).name = `imageGeneratedByAI-${Date.now()}`;
        (blob as any).lastModifiedDate = new Date();
        const files = yield* select(getAllBackgrounds);
        const isFirstTime = files.length === 0;
        const newFiles = [
            {
                id: isFirstTime ? idGenerator() : idAddGenerator(),
                file: blob,
                isFirstUpload: isFirstTime,
            },
        ];
        yield* put(uploadBackgrounds(newFiles as any));
    }
}

export default function* applicationSagas() {
    return yield* all([
        yield* takeEvery(loadApplication, loadApp),
        yield* takeEvery(setTenant as any, getTenantMerchandisingSettings),
        yield* takeEvery(setResources as any, getTenantMerchandisingSettings),
        yield* takeEvery(confirmImageAI, createSceneFromAI),
        yield* takeEvery(changeMode, changeModals),
    ]);
}
